import PropTypes from 'prop-types'
import React from 'react'
import LearnErrorBoundary from 'components/LearnErrorBoundary'
import LessonList from 'experienceComponents/LessonList'
import RecommendedAdvisoryActivities from 'experienceComponents/RecommendedAdvisoryActivities'
import UnitOverview from 'experienceComponents/UnitOverview'
import UnitTabSet from 'experienceComponents/UnitTabSet'
import VideoCallout from 'experienceComponents/VideoCallout'
import {
  AcknowledgementsLink,
  UnitBodyBackground,
  UnitBodyContent,
  UnitComponentWrapper,
  UnitOverviewBox,
  UnitTopContent,
} from './component.styles'
import { UnitType } from './types'

const UnitComponent = ({
  acknowledgements,
  gradeTheme,
  isLessonAccordion,
  lessonStartingIndex,
  lessonSummaries = [],
  lmsLessons,
  match,
  onAcknowledgementsClick,
  onSelectUnit,
  productName,
  showAdditionalResources,
  unit,
  unitId,
  unitName,
  unitTabs,
  unitVideo,
}) => {
  const acknowledgementsLink = acknowledgements && (
    <AcknowledgementsLink
      acknowledgementsDataNOTCONNECTED={acknowledgements}
      dataTestId="acknowledgements-link"
      onClick={onAcknowledgementsClick}
    >
      Acknowledgements
    </AcknowledgementsLink>
  )

  const overviewContent = (
    <UnitOverviewBox id="unit-overviewbox" role={'tabpanel'}>
      <UnitOverview
        contentfulItem={unit}
        dataTestId={`unit-overview-${unitId}`}
        tabIndex={0}
      />
      {unitVideo && (
        <VideoCallout
          dataTestId={`video-callout-${unitId}`}
          id="video-callout"
          locale={'en-US'}
          videoDisplayTitle={unitVideo.displayTitle}
          videoId={unitVideo.id}
          videoLength={unitVideo.time}
          videoThumbnailUrl={unitVideo.videoReference.posterImage?.url}
        />
      )}
    </UnitOverviewBox>
  )

  const lessonList = (
    <LessonList
      contentfulLessons={unit.lessons}
      dataTestId="lesson-list"
      displayAsAccordion={isLessonAccordion}
      gradeTheme={gradeTheme}
      lessonStartingIndex={lessonStartingIndex}
      lessonSummaries={isLessonAccordion ? undefined : lessonSummaries}
      lessons={lmsLessons}
      match={match}
      unitResources={unit.unitResources}
    />
  )

  const additionalResources = showAdditionalResources && (
    <RecommendedAdvisoryActivities
      contentfulItem={unit}
      match={match}
      productName={productName}
      unitName={unitName}
    />
  )

  const unitTabSet = (
    <UnitTabSet
      ariaControls={'unit-overviewbox'}
      match={match}
      onSelect={onSelectUnit}
      selectedUnitSlug={unitId}
      units={unitTabs}
    />
  )

  return (
    <LearnErrorBoundary>
      {isLessonAccordion ? (
        <>
          <UnitComponentWrapper animation={'fadeIn'}>
            {unitTabSet}
            {overviewContent}
            {lessonList}
            {additionalResources}
          </UnitComponentWrapper>
          {acknowledgementsLink}
        </>
      ) : (
        <UnitComponentWrapper animation={'fadeIn'}>
          <UnitTopContent>{unitTabSet}</UnitTopContent>
          <UnitBodyBackground $hasSideCallout={Boolean(unitVideo)}>
            {overviewContent}
            <UnitBodyContent>
              {lessonList}
              {additionalResources}
            </UnitBodyContent>
          </UnitBodyBackground>
          {acknowledgementsLink}
        </UnitComponentWrapper>
      )}
    </LearnErrorBoundary>
  )
}

UnitComponent.propTypes = {
  acknowledgements: PropTypes.object,
  gradeTheme: PropTypes.string,
  isLessonAccordion: PropTypes.bool.isRequired,
  lessonStartingIndex: PropTypes.number.isRequired,
  lessonSummaries: PropTypes.array,
  lmsLessons: PropTypes.array.isRequired,
  match: PropTypes.object.isRequired,
  onAcknowledgementsClick: PropTypes.func,
  onSelectUnit: PropTypes.func.isRequired,
  productName: PropTypes.string,
  showAdditionalResources: PropTypes.bool.isRequired,
  unit: UnitType.isRequired,
  unitId: PropTypes.string,
  unitName: PropTypes.string.isRequired,
  unitTabs: PropTypes.array.isRequired,
  unitVideo: PropTypes.object,
}

UnitComponent.displayName = 'UnitComponent'

export default UnitComponent
